import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Col, Divider, Empty, Form, Input, Row, Select, Space } from 'antd';
import { fetchData, saveData } from '../../service/dataServices';
import { getFormItems } from '../../components/form/formComponents';
import CustomAddNewPageContent from '../../../components/Content/CustomAddNewPageContent';
import moment from 'moment';
import { getOperationType, rowStyle } from '../../components/common-constants';
import { hasWritePermission } from '../../../components/Authorization/hasWritePermission';

const { Option } = Select;

const SaveCondition = () => {
  const PATH = '/api/conditions';
  const { id } = useParams();
  const operation = getOperationType(id);
  const hasWritePerm = hasWritePermission("condition");

  const [formObject] = Form.useForm();
  const [form] = Form.useForm();
  const [mainImageFileList, setMainImageFileList] = useState([]);
  const [imageList, setImageList] = useState([]);
  const [initialData, setInitialData] = useState();
  const [dropdowns, setDropdowns] = useState();
  const [initialOtherDimensions, setInitialOtherDimensions] = useState([]);
  const [objects, setObjects] = useState([]);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [objectFilters, setObjectFilters] = useState({
    page: 0,
    size: 20,
  });

  const {
    getDropdownFormItem,
    getMultiselectFormItem,
    getInputFormItem,
    getInputNumberFormItem,
    getDatePickerFormItem,
    getImageListFormItem,
    getMainImageFormItem,
    getTextAreaFormItem,
    getFormSubmitButton,
    getOtherDimensionsFormItems,
  } = getFormItems(dropdowns, hasWritePerm);

  fetchData('/api/dropdowns', setDropdowns);
  if (id) {
    fetchData(PATH + '/' + id, setInitialData);
  }
  const objectResponse = fetchData('/api/objects', data => setObjects(data.content), objectFilters);
  const saveMutation = saveData(PATH, operation.method);
  const fetchObjects = fetchData('/api/objects/' + selectedObjectId, setSelectedObject, null, false);

  useEffect(() => {
    if (selectedObjectId) {
      fetchObjects.refetch().then(resp => {
        let data = resp.data.data;
        const found = objects.find(o => o.id === data.id);
        if (!found) {
          setObjects(prev => [...prev, {id: data.id, inventoryNumber: data.inventoryNumber }])
        }
      })
    }
  }, [selectedObjectId]);

  useEffect(() => {
    if (selectedObject) {
      setInitialOtherDimensions(selectedObject.otherDimensions || []);
      if (selectedObject.mainImage) {
        setMainImageFileList([selectedObject.mainImage]);
      } else {
        setMainImageFileList([]);
      }
      formObject.setFieldsValue(selectedObject);
    }
  }, [selectedObject]);

  useEffect(() => {
    if (initialData && dropdowns) {
      setSelectedObjectId(initialData.objectId);
      if (initialData.imageList) setImageList(initialData.imageList);
      if (initialData.conditionDate) {
        form.setFieldsValue({
          ...initialData,
          conditionDate: moment(initialData.conditionDate),
        });
      } else {
        form.setFieldsValue(initialData);
      }
    }
  }, [initialData, dropdowns]);

  const onSearch = val => {
    if (val.length >= 4) {
      setObjectFilters((prev) => {
        return ({ ...prev, inventoryNumber: val });
      });
    }
  };

  const onClearSelectedObject = () => {
    setSelectedObjectId(null);
    setSelectedObject(null);
    formObject.resetFields();
    setInitialOtherDimensions([]);
    setMainImageFileList([]);
  };

  const onFinish = (values) => {
    const dataToSend = {
      ...values,
      objectId: selectedObjectId,
      imageList: imageList ? imageList : undefined,
    };
    if (id) {
      saveMutation.mutate({ dataToSend, id });
    } else {
      saveMutation.mutate({ dataToSend });
    }
  };

  return (
    <CustomAddNewPageContent title={operation.type + ' Condition'} subTitle="Conditions">
      <Row style={{ marginLeft: 20, marginRight: 20 }}>
        <Select
          showSearch
          placeholder="Find object by inventory number"
          allowClear
          value={selectedObjectId}
          onClear={onClearSelectedObject}
          onSelect={value => setSelectedObjectId(value)}
          onSearch={onSearch}
          filterOption={false}
          loading={objectResponse.isFetching}
          style={{ width: '36vh' }}
        >
          {
            objects.map(option => (
              <Option value={option.id} key={option.id}>{option.inventoryNumber}</Option>
            ))
          }
        </Select>
      </Row>
      <Divider/>
      <Form
        form={formObject}
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 16 }}
        layout="vertical"
        autoComplete="off"
      >
        <Row style={rowStyle}>
          <Col span={8} style={{ display: 'flex', alignItems: 'center' }}>
            {getMainImageFormItem('mainImage', true, 'objects', mainImageFileList, setMainImageFileList)}
            {mainImageFileList && mainImageFileList.length === 0 && (
              <div>
                <Space direction="vertical">
                  <Empty description={false}/>
                </Space>
              </div>
            )}
          </Col>
          <Col span={8}>
            {getInputFormItem('inventoryNumber', 'Inventory Number', false, 'text', true)}
            {getInputFormItem('objectName', 'Object Name', false, 'text', true)}
            {getInputFormItem('objectTitle', 'Object Title', false, 'text', true)}
            {getMultiselectFormItem('material', 'Material', true)}
            {getMultiselectFormItem('technique', 'Technique', true)}
          </Col>
          <Col span={8}>
            {getInputNumberFormItem('length', 'Length', 'cm', true)}
            {getInputNumberFormItem('width', 'Width', 'cm', true)}
            {getInputNumberFormItem('height', 'Height', 'cm', true)}
            {getInputNumberFormItem('weight', 'Weight', 'gr', true)}
            {getOtherDimensionsFormItems(initialOtherDimensions, true)}
          </Col>
        </Row>
        <Row style={rowStyle}>
          <Col span={24}>
            {getTextAreaFormItem('description', 'Description', { span: 22 }, true)}
          </Col>
        </Row>
      </Form>
      <Divider/>
      <Form
        form={form}
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 16 }}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row style={rowStyle}>
          <Col span={12}>
            {getDropdownFormItem('condition', 'Condition')}
            {getTextAreaFormItem('maintenanceNotes', 'Maintenance Notes')}
            {getDropdownFormItem('examinationTechnique', 'Examination Technique')}
            {getDropdownFormItem('locationOfExamination', 'Location of Examination')}
            {getInputFormItem('city', 'City')}
          </Col>
          <Col span={12}>
            {getInputFormItem('conditionSummary', 'Condition Summary')}
            {getInputNumberFormItem('temperatureMin', 'Temperature Min', 'C')}
            {getInputNumberFormItem('temperatureMax', 'Temperature Max', 'C')}
            {getInputNumberFormItem('humidity', 'Humidity', 'RH')}
            {getInputNumberFormItem('light', 'Light', 'Lux')}
          </Col>
        </Row>
        <Row style={rowStyle}>
          <Col span={12}>
            {getDatePickerFormItem('conditionDate', 'Condition Date')}
          </Col>
          <Col span={12}>
            {getInputFormItem('examinedBy', 'Examined By')}
          </Col>
        </Row>
        <Divider/>
        <Row style={rowStyle}>
          {getImageListFormItem('imageList', false, 'conditions', imageList, setImageList)}
        </Row>
        <Divider/>
        {getFormSubmitButton(!selectedObject)}
      </Form>

    </CustomAddNewPageContent>
  );
};

export default SaveCondition;
